import { STORAGE_KEYS } from "@lib/config/storageKeys";
import { useCallback, useEffect, useRef, useState } from "react";

export interface GeolocationPosition {
  latitude: number;
  longitude: number;
  accuracy: number;
  timestamp: number;
}

export interface GeolocationState {
  loading: boolean;
  accuracy: number | null;
  latitude: number | null;
  longitude: number | null;
  error: string | null;
  timestamp: number | null;
  failureCount: number;
  consistentlyFailing: boolean;
}

const FAILURE_THRESHOLD = 3;

export interface UseGeolocationOptions {
  enableHighAccuracy?: boolean;
  timeout?: number;
  maximumAge?: number;
  /** Maximum accuracy threshold in meters - overrides default 1000 */
  maxAccuracy?: number;
}

export interface UseGeolocationReturn {
  loading: boolean;
  error: string | null;
  position: GeolocationPosition | null;
  isUsingTestPosition: boolean;
  failureCount: number;
  consistentlyFailing: boolean;
  manualOverride: boolean;
  enableManualOverride: () => void;
  disableManualOverride: () => void;
  getCurrentPosition: () => Promise<GeolocationPosition>;
  testGeolocationNow: () => Promise<GeolocationPosition | null>;
  getTestPosition: () => GeolocationPosition | null;
  clearTestPosition: () => void;
  resetError: () => void;
  isPositionValid: (accuracy?: number) => boolean;
  watch: (callback: (pos: GeolocationPosition | null, err: Error | null) => void) => void;
  clearWatch: () => void;
}

const useGeolocation = (options: UseGeolocationOptions = {}): UseGeolocationReturn => {
  const isTestEnabled = import.meta.env.DEV;

  // Check for persisted manual override from localStorage
  const getStoredOverride = (): boolean => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem(STORAGE_KEYS.FEATURES.POINTAGE.MANUAL_MODE_OVERRIDE) === "true";
  };

  const [manualOverride, setManualOverride] = useState<boolean>(false);

  const [state, setState] = useState<GeolocationState>({
    loading: false,
    accuracy: null,
    latitude: null,
    longitude: null,
    error: null,
    timestamp: null,
    failureCount: 0,
    consistentlyFailing: false,
  });

  const [testPosition, setTestPosition] = useState<GeolocationPosition | null>(null);
  const watchId = useRef<number | null>(null);

  const defaultOptions: PositionOptions = {
    enableHighAccuracy: false, // Try false for faster/more reliable GPS
    timeout: 30000,
    maximumAge: 60000, // Cache position for 1 minute
  };

  const maxAccuracyThreshold = options.maxAccuracy ?? 1000;

  const getErrorMsg = (err: GeolocationPositionError | Error): string => {
    if ("code" in err) {
      switch (err.code) {
        case 1:
          return "Permission refusée. Cliquez sur l'icône 📍 dans la barre d'adresse et autorisez.";
        case 2:
          return "GPS non disponible. Vérifiez votre connexion ou activez votre GPS.";
        case 3:
          return "Timeout. Le GPS met trop de temps à répondre.";
      }
    }
    return err.message || "Erreur inconnue";
  };

  const getGeolocation = useCallback(() => {
    return new Promise<GeolocationPosition>((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error("Géolocalisation non supportée par ce navigateur"));
        return;
      }

      // Try high accuracy first, then fallback to network
      const tryGetPosition = (enableHighAcc: boolean) => {
        const opts: PositionOptions = {
          enableHighAccuracy: enableHighAcc,
          timeout: enableHighAcc ? 15000 : 30000,
          maximumAge: 60000,
        };

        navigator.geolocation.getCurrentPosition(
          (position) => {
            resolve({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              accuracy: position.coords.accuracy,
              timestamp: position.timestamp,
            });
          },
          (err) => {
            let msg = err.message || "";
            if (msg.includes("permission") || err.code === 1) {
              msg =
                "Permission refusée. Cliquez sur l'icône 📍 dans la barre d'adresse et autorisez.";
            } else if (msg.includes("unavailable") || msg.includes("Unknown") || err.code === 2) {
              msg = "GPS non disponible. Vérifiez votre connexion ou activez votre GPS.";
            } else {
              msg = "GPS non disponible. Vérifiez votre connexion ou activez votre GPS.";
            }
            console.log("GPS final msg:", msg);
            if (!enableHighAcc) {
              reject(new Error(msg));
            } else {
              tryGetPosition(false);
            }
          },
          opts,
        );
      };

      tryGetPosition(true);
    });
  }, []);

  const getCurrentPosition = useCallback(async () => {
    setState((prevState) => ({ ...prevState, loading: true, error: null }));

    try {
      const position = await getGeolocation();
      setState({
        loading: false,
        ...position,
        error: null,
        failureCount: 0,
        consistentlyFailing: false,
      });
      return position;
    } catch (error) {
      const msg = (error instanceof Error ? error.message : null) || "Erreur de géolocalisation";
      const newFailureCount = state.failureCount + 1;
      const isConsistentlyFailing = newFailureCount >= FAILURE_THRESHOLD;

      setState({
        loading: false,
        accuracy: null,
        latitude: null,
        longitude: null,
        error: msg,
        timestamp: null,
        failureCount: newFailureCount,
        consistentlyFailing: isConsistentlyFailing,
      });
      throw new Error(msg);
    }
  }, [getGeolocation, state.failureCount]);

  const testGeolocationNow = useCallback(async () => {
    if (!isTestEnabled) {
      setState((prevState) => ({
        ...prevState,
        error: "Mode test non disponible en production",
      }));
      return null;
    }

    if (!navigator.geolocation) {
      setState({
        loading: false,
        accuracy: null,
        latitude: null,
        longitude: null,
        error: "Géolocalisation non supportée par ce navigateur",
        timestamp: null,
        failureCount: 0,
        consistentlyFailing: false,
      });
      return null;
    }

    try {
      const position = await getGeolocation();
      setTestPosition(position);
      return position;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? getErrorMessage(error) : "Erreur de géolocalisation";
      setState((prevState) => ({
        ...prevState,
        error: errorMessage,
      }));
      throw new Error(errorMessage);
    }
  }, [getGeolocation, isTestEnabled]);

  const getTestPosition = useCallback(() => {
    if (!isTestEnabled) {
      setState((prevState) => ({
        ...prevState,
        error: "Mode test non disponible en production",
      }));
      return null;
    }

    const testCoords: GeolocationPosition = {
      latitude: 48.8566,
      longitude: 2.3522,
      accuracy: 10,
      timestamp: Date.now(),
    };

    setTestPosition(testCoords);
    setState({
      loading: false,
      ...testCoords,
      error: null,
      failureCount: 0,
      consistentlyFailing: false,
    });

    return testCoords;
  }, [isTestEnabled]);

  const clearTestPosition = useCallback(() => {
    setTestPosition(null);
  }, []);

  const resetError = useCallback(() => {
    setState((prevState) => ({
      ...prevState,
      error: null,
      failureCount: 0,
      consistentlyFailing: false,
    }));
  }, []);

  const isPositionValid = useCallback(
    (position?: GeolocationPosition | null) => {
      if (!position) return false;
      // Must have valid lat/lng (not null/0) and accuracy within threshold
      return (
        position.latitude !== null &&
        position.longitude !== null &&
        position.latitude !== 0 &&
        position.longitude !== 0 &&
        position.accuracy !== null &&
        position.accuracy <= maxAccuracyThreshold
      );
    },
    [maxAccuracyThreshold],
  );

  const enableManualOverride = useCallback(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem(STORAGE_KEYS.FEATURES.POINTAGE.MANUAL_MODE_OVERRIDE, "true");
      setManualOverride(true);
    }
  }, []);

  const disableManualOverride = useCallback(() => {
    if (typeof window !== "undefined") {
      localStorage.removeItem(STORAGE_KEYS.FEATURES.POINTAGE.MANUAL_MODE_OVERRIDE);
      setManualOverride(false);
      // Reset failure counter when re-enabling geolocation
      setState((prevState) => ({ ...prevState, failureCount: 0, consistentlyFailing: false }));
    }
  }, []);

  const currentPosition: GeolocationPosition | null = testPosition || {
    latitude: state.latitude ?? 0,
    longitude: state.longitude ?? 0,
    accuracy: state.accuracy ?? 0,
    timestamp: state.timestamp ?? 0,
  };

  return {
    loading: state.loading,
    error: state.error,
    position: currentPosition,
    isUsingTestPosition: !!testPosition,
    failureCount: state.failureCount,
    consistentlyFailing: state.consistentlyFailing,
    manualOverride,
    enableManualOverride,
    disableManualOverride,
    getCurrentPosition,
    testGeolocationNow,
    getTestPosition,
    clearTestPosition,
    resetError,
    isPositionValid: (accuracy?: number | GeolocationPosition) => {
      if (typeof accuracy === "object" && accuracy !== null) {
        return isPositionValid(accuracy);
      }
      return isPositionValid(currentPosition);
    },
    watch: (callback: (pos: GeolocationPosition | null, err: Error | null) => void) => {
      if (!navigator.geolocation) {
        callback(null, new Error("Géolocalisation non supportée"));
        return;
      }

      watchId.current = navigator.geolocation.watchPosition(
        (position) => {
          const pos: GeolocationPosition = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            timestamp: position.timestamp,
          };
          setState({
            loading: false,
            ...pos,
            error: null,
            failureCount: 0,
            consistentlyFailing: false,
          });
          callback(pos, null);
        },
        (error) => {
          const errorMessage = getErrorMessage(error);
          setState({
            loading: false,
            accuracy: null,
            latitude: null,
            longitude: null,
            error: errorMessage,
            timestamp: null,
            failureCount: 0,
            consistentlyFailing: false,
          });
          callback(null, new Error(errorMessage));
        },
        defaultOptions,
      );
    },

    clearWatch: () => {
      if (watchId.current) {
        navigator.geolocation.clearWatch(watchId.current);
        watchId.current = null;
      }
    },
  };
};

function getErrorMessage(error: GeolocationPositionError | Error): string {
  // Debug - log actual error for troubleshooting
  console.log("GPS error:", error, "code:", "code" in error ? error.code : undefined);

  // Check if it's a GeolocationPositionError with known code
  if ("code" in error) {
    console.log("GPS error code:", error.code);
    switch (error.code) {
      case 1: // PERMISSION_DENIED
        return "Permission refusée. Cliquez sur l'icône 📍 dans la barre d'adresse et autorisez.";
      case 2: // POSITION_UNAVAILABLE
        return "GPS non disponible. Vérifiez votre connexion ou activez votre GPS.";
      case 3: // TIMEOUT
        return "Timeout. Le GPS met trop de temps à répondre.";
    }
  }
  // Fallback - try to extract useful info
  const msg = error.message || "";
  if (msg.includes("permission")) {
    return "Permission refusée. Cliquez sur l'icône 📍 dans la barre d'adresse et autorisez.";
  }
  if (msg.includes("unavailable") || msg.includes("Unknown")) {
    return "GPS non disponible. Vérifiez votre connexion ou activez votre GPS.";
  }
  return "Erreur de géolocalisation. Veuillez réessayer.";
}

// Manual override disabled for pointage feature
export const disableManualOverride = (): void => {
  // Feature disabled - no-op for compatibility
};

export default useGeolocation;
